
import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import { isExternalApp } from "utils/appConfig.js"

/**
 * Send tracking event to the seller app tracking endpoint
 * 
 * @param {Object} params - Tracking parameters
 * @param {string} params.source_page - The page/screen where the event occurred
 * @param {string} params.component_type - Type of component (e.g., "BUTTON", "LINK", "PAGE")
 * @param {string} params.component_name - Name/identifier of the component
 * @param {string} params.action - Action performed (e.g., "CLICK", "TAP", "PAGE_LOAD")
 * @returns {Promise} - Promise that resolves when tracking is sent
 * 
 * @example
 * // Track a button click
 * sendTracking({
 *   source_page: "HOME_PAGE",
 *   component_type: "BUTTON",
 *   component_name: "VIEW_ALL_POS",
 *   action: "CLICK"
 * });
 * 
 * @example
 * // Track a page load
 * sendTracking({
 *   source_page: "PO_DETAILS",
 *   component_type: "PAGE",
 *   component_name: "PO_DETAILS_PAGE",
 *   action: "PAGE_LOAD"
 * });
 */
export const sendTracking = async (params) => {
  // Tracking is a seller-app-only concern; external portal must not hit /public/api/sellerapp/tracking.
  if (isExternalApp()) {
    return;
  }
  try {
    const {
      source_page,
      component_type,
      component_name,
      action,
    } = params;

    // Validate required parameters
    if (!source_page || !component_type || !component_name || !action) {
      console.warn("[Tracking] Missing required parameters:", {
        source_page,
        component_type,
        component_name,
        action,
      });
      return;
    }

    // Prepare tracking payload
    const trackingPayload = {
      source_page,
      component_type,
      component_name,
      action,
      request_timestamp_unix: Math.floor(Date.now() / 1000),
    };

    // Prepare tracking-specific headers with referred_platform_type as appsource
    const referredPlatformType = window.localStorage.getItem(LS_KEYS.REFERRED_PLATFORM_TYPE);
    const trackingOptions = referredPlatformType
      ? { headers: { appsource: referredPlatformType } }
      : {};

    // Send tracking request
    await api.post(API_HOST_WMS, APIendpoints.sendSellerAppTracking, trackingPayload, trackingOptions);
  } catch (error) {
    // Silent fail - tracking should not break the app
    console.error("[Tracking] Failed to send tracking event:", error);
  }
};

/**
 * Track a click/tap action
 * 
 * @param {string} sourcePage - The page where the click occurred
 * @param {string} componentName - Name of the clicked component
 * @param {string} componentType - Type of component (default: "BUTTON")
 * 
 * @example
 * trackClick("HOME_PAGE", "PENDING_POS_CARD", "CARD");
 */
export const trackClick = (sourcePage, componentName, componentType = "BUTTON") => {
  return sendTracking({
    source_page: sourcePage,
    component_type: componentType,
    component_name: componentName,
    action: "CLICK",
  });
};

/**
 * Track a page load event
 * 
 * @param {string} pageName - Name of the page being loaded
 * 
 * @example
 * trackPageLoad("PO_DETAILS");
 */
export const trackPageLoad = (pageName) => {
  return sendTracking({
    source_page: pageName,
    component_type: "PAGE",
    component_name: `${pageName}_PAGE`,
    action: "IMPRESSION",
  });
};

/**
 * Track a tap action (alias for trackClick)
 * 
 * @param {string} sourcePage - The page where the tap occurred
 * @param {string} componentName - Name of the tapped component
 * @param {string} componentType - Type of component (default: "BUTTON")
 * 
 * @example
 * trackTap("GRN_DETAILS", "CONFIRM_GRN_BUTTON");
 */
export const trackTap = trackClick;
